import React, { useMemo, useState, useEffect } from 'react';
import { format, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  TrendingUp,
  TrendingDown,
  Clock,
  DollarSign,
  Users,
  Briefcase,
  Calendar,
  Download,
  Filter,
  BarChart3,
  Activity,
  Target,
  AlertCircle,
  MousePointerClick,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useTimesheet } from '@/contexts/TimesheetContext';
import { generateUtilisationReport, exportUtilisationToCSV } from '@/utils/utilisationUtils';
import type { UtilisationFilters, UtilisationMetrics, ProjectUtilisation, FilterPreset, UtilisationReportData } from '@/types/utilisation';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { MultiSelect } from '@/components/ui/multiselect';
import { DateRangePicker } from '@/components/UtilisationReport/DateRangePicker';
import { EmployeeDetailModal } from '@/components/UtilisationReport/EmployeeDetailModal';
import { ProjectDetailModal } from '@/components/UtilisationReport/ProjectDetailModal';
import { FilterPresetManager } from '@/components/UtilisationReport/FilterPresetManager';
import { QuickFilters } from '@/components/UtilisationReport/QuickFilters';
import { ActiveFilterChips } from '@/components/UtilisationReport/ActiveFilterChips';
import { AdvancedFilterDrawer } from '@/components/UtilisationReport/AdvancedFilterDrawer';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import * as settingsRepository from '@/services/settingsRepository';
import type { UtilizationSettings } from '@/types/timesheetSettings';
import { utilisationApi } from '@/services/utilisationApi';

// Date range presets
const DATE_RANGES = {
  last7days: { label: 'Last 7 Days', days: 7 },
  last30days: { label: 'Last 30 Days', days: 30 },
  last90days: { label: 'Last 90 Days', days: 90 },
  thisMonth: { label: 'This Month', days: 0 },
  lastMonth: { label: 'Last Month', days: 0 },
  last6months: { label: 'Last 6 Months', days: 180 },
};

export const UtilisationReportPage: React.FC = () => {
  const { timesheets, employees, projects } = useTimesheet();
  const { mode, isLive } = useScreenDataMode('reports-utilization');
  
  // Debug logging to verify mode values
  console.log('[UtilisationReportPage] mode:', mode, 'isLive:', isLive);
  
  // Settings state - Load organization utilization settings
  const [utilizationSettings, setUtilizationSettings] = useState<UtilizationSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  
  // API data state - for live mode
  const [apiReportData, setApiReportData] = useState<UtilisationReportData | null>(null);
  const [apiLoading, setApiLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  
  // Phase 1 state
  const [selectedRange, setSelectedRange] = useState<keyof typeof DATE_RANGES>('last30days');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [selectedGroupBy, setSelectedGroupBy] = useState<'employee' | 'department' | 'project'>(
    'employee'
  );
  const [activeTab, setActiveTab] = useState('overview');

  // Phase 2 state - Date mode and custom range
  const [dateMode, setDateMode] = useState<'preset' | 'custom'>('preset');
  const [customDateRange, setCustomDateRange] = useState<{
    start: Date | undefined;
    end: Date | undefined;
  }>({ start: undefined, end: undefined });

  // Phase 2 state - Multi-select employees
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);

  // Phase 2 state - Filter presets
  const [activePresetId, setActivePresetId] = useState<string | null>(null);

  // Phase 2 state - Modals
  const [selectedEmployeeModal, setSelectedEmployeeModal] = useState<UtilisationMetrics | null>(null);
  const [selectedProjectModal, setSelectedProjectModal] = useState<ProjectUtilisation | null>(null);

  // New filter state - Phase 3 (Filter Consistency)
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [billableOnly, setBillableOnly] = useState<boolean>(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState<boolean>(false);
  const [quickFilterEmployee, setQuickFilterEmployee] = useState<string>('all');

  // Collapsible filters state (Space Optimization) - DEPRECATED, using new filter system
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(() => {
    const saved = localStorage.getItem('utilisationReportFiltersExpanded');
    return saved !== null ? JSON.parse(saved) : false; // Default: collapsed
  });

  // Save filter expansion preference to localStorage
  useEffect(() => {
    localStorage.setItem('utilisationReportFiltersExpanded', JSON.stringify(filtersExpanded));
  }, [filtersExpanded]);

  // Load organization utilization settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setSettingsLoading(true);
        const orgSettings = await settingsRepository.getOrganizationTimesheetSettings(mode);
        setUtilizationSettings(orgSettings.utilization);
      } catch (error) {
        console.error('Failed to load utilization settings:', error);
        // Use default settings if loading fails
        setUtilizationSettings({
          targetUtilizationRate: 0.75,
          calculationMethod: 'billable-total',
        });
      } finally {
        setSettingsLoading(false);
      }
    };

    loadSettings();
  }, [mode]);

  // Calculate date range
  const dateRange = useMemo(() => {
    // Phase 2: Support custom date range
    if (dateMode === 'custom' && customDateRange.start && customDateRange.end) {
      return {
        start: customDateRange.start,
        end: customDateRange.end,
      };
    }

    // Phase 1: Preset date ranges
    const today = new Date();
    const range = DATE_RANGES[selectedRange];

    if (selectedRange === 'thisMonth') {
      return {
        start: startOfMonth(today),
        end: endOfMonth(today),
      };
    } else if (selectedRange === 'lastMonth') {
      const lastMonth = subMonths(today, 1);
      return {
        start: startOfMonth(lastMonth),
        end: endOfMonth(lastMonth),
      };
    } else {
      return {
        start: subDays(today, range.days),
        end: today,
      };
    }
  }, [selectedRange, dateMode, customDateRange]);

  // Get unique departments
  const departments = useMemo(() => {
    const depts = new Set(employees.map((e) => e.department));
    return Array.from(depts).sort();
  }, [employees]);

  // Generate filters
  const filters: UtilisationFilters = useMemo(
    () => ({
      dateRange,
      departments: selectedDepartments.length > 0 
        ? selectedDepartments 
        : (selectedDepartment === 'all' ? [] : [selectedDepartment]),
      employees: selectedEmployees,
      projects: selectedProjects,
      includeNonBillable: !billableOnly,
      groupBy: selectedGroupBy,
    }),
    [dateRange, selectedDepartment, selectedDepartments, selectedEmployees, selectedProjects, billableOnly, selectedGroupBy]
  );

  // Load utilisation data from API when in live mode
  useEffect(() => {
    console.log('[UtilisationReportPage useEffect] Running with isLive:', isLive, 'filters:', filters);
    
    if (!isLive) {
      // In demo mode, clear API data and use mock data
      console.log('[UtilisationReportPage useEffect] Not in live mode, clearing API data');
      setApiReportData(null);
      setApiError(null);
      return;
    }

    console.log('[UtilisationReportPage useEffect] In live mode, loading API data');

    const loadApiData = async () => {
      try {
        setApiLoading(true);
        setApiError(null);

        const params = {
          dateFrom: filters.dateRange.start,
          dateTo: filters.dateRange.end,
          employeeIds: filters.employees.length > 0 ? filters.employees : undefined,
          departments: filters.departments.length > 0 ? filters.departments : undefined,
          projectIds: filters.projects.length > 0 ? filters.projects : undefined,
          includeNonBillable: filters.includeNonBillable,
          groupBy: filters.groupBy,
        };

        console.log('[UtilisationReportPage] Calling API with params:', params);
        const data = await utilisationApi.getUtilisationReport(params);
        console.log('[UtilisationReportPage] API response received:', data);
        setApiReportData(data);
      } catch (error) {
        console.error('Failed to load utilisation data from API:', error);
        setApiError(error instanceof Error ? error.message : 'Failed to load data');
        toast.error('Failed to load utilisation data from API');
      } finally {
        setApiLoading(false);
      }
    };

    loadApiData();
  }, [isLive, filters]);

  // Generate report data - use API data in live mode, mock data in demo mode
  const reportData = useMemo(() => {
    if (isLive && apiReportData) {
      // Use API data in live mode
      console.log('[UtilisationReportPage] Using API data for report');
      return apiReportData;
    }
    // Use mock data in demo mode
    console.log('[UtilisationReportPage] Using mock data for report');
    return generateUtilisationReport(timesheets, employees, projects, filters, utilizationSettings || undefined);
  }, [isLive, apiReportData, timesheets, employees, projects, filters, utilizationSettings]);

  // Handle export
  const handleExport = () => {
    try {
      const csv = exportUtilisationToCSV(reportData);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `utilisation-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  // Group employee metrics by department if needed
  const groupedMetrics = useMemo(() => {
    if (selectedGroupBy === 'department') {
      const grouped: { [key: string]: typeof reportData.employeeMetrics } = {};
      reportData.employeeMetrics.forEach((metric) => {
        if (!grouped[metric.department]) {
          grouped[metric.department] = [];
        }
        grouped[metric.department].push(metric);
      });
      return grouped;
    }
    return { All: reportData.employeeMetrics };
  }, [reportData.employeeMetrics, selectedGroupBy]);

  // Calculate department summaries
  const departmentSummaries = useMemo(() => {
    const summaries: {
      [key: string]: {
        totalHours: number;
        billableHours: number;
        employeeCount: number;
        avgUtilisation: number;
      };
    } = {};

    Object.entries(groupedMetrics).forEach(([dept, metrics]) => {
      const totalHours = metrics.reduce((sum, m) => sum + m.totalHours, 0);
      const billableHours = metrics.reduce((sum, m) => sum + m.billableHours, 0);
      const avgUtilisation =
        metrics.length > 0
          ? metrics.reduce((sum, m) => sum + m.utilisationRate, 0) / metrics.length
          : 0;

      summaries[dept] = {
        totalHours,
        billableHours,
        employeeCount: metrics.length,
        avgUtilisation,
      };
    });

    return summaries;
  }, [groupedMetrics]);

  const { summary } = reportData;

  // Helper function to get utilization accent class
  const getUtilizationAccent = (utilization: number): string => {
    if (utilization >= 0.85) return 'accent-success';
    if (utilization >= 0.75) return 'accent-info';
    if (utilization >= 0.60) return 'accent-warning';
    return 'accent-destructive';
  };

  // Handle summary card clicks for interactive filtering
  const handleSummaryCardClick = (cardType: string) => {
    switch (cardType) {
      case 'underutilized':
        // Filter to show employees below target
        setActiveTab('employees');
        toast.info('Showing employees below target utilisation');
        break;
      case 'billable':
        // Switch to projects tab to show billable projects
        setActiveTab('projects');
        toast.info('Showing project allocation');
        break;
      case 'employees':
        // Switch to employees tab
        setActiveTab('employees');
        break;
      case 'peak':
        // Switch to overview to see daily patterns
        setActiveTab('overview');
        toast.info('Showing daily utilisation patterns');
        break;
    }
  };

  // Prepare chart data for trends
  const trendChartData = useMemo(() => {
    return reportData.trends.map((trend) => ({
      week: format(trend.weekEnding, 'MMM d'),
      'Utilisation %': (trend.utilisationRate * 100).toFixed(1),
      'Billable Hours': trend.billableHours.toFixed(1),
      'Total Hours': trend.totalHours.toFixed(1),
    }));
  }, [reportData.trends]);

  // Prepare chart data for daily utilisation
  const dailyChartData = useMemo(() => {
    return reportData.dailyUtilisation.map((day) => ({
      day: day.day.charAt(0).toUpperCase() + day.day.slice(1, 3),
      Billable: day.billableHours.toFixed(1),
      'Non-Billable': day.nonBillableHours.toFixed(1),
      utilisationRate: day.utilisationRate,
    }));
  }, [reportData.dailyUtilisation]);

  // Phase 2 handlers

  // Handle preset load
  const handleLoadPreset = (preset: FilterPreset) => {
    setDateMode(preset.filters.dateMode);
    if (preset.filters.dateMode === 'custom' && preset.filters.customDateRange) {
      setCustomDateRange(preset.filters.customDateRange);
    } else if (preset.filters.selectedRange) {
      setSelectedRange(preset.filters.selectedRange as keyof typeof DATE_RANGES);
    }
    setSelectedDepartment(preset.filters.selectedDepartment);
    setSelectedEmployees(preset.filters.selectedEmployees);
    setSelectedGroupBy(preset.filters.selectedGroupBy);
    setActivePresetId(preset.id);
  };

  // Handle preset clear
  const handleClearPreset = () => {
    setActivePresetId(null);
  };

  // Handle employee modal
  const handleEmployeeClick = (employee: UtilisationMetrics) => {
    setSelectedEmployeeModal(employee);
  };

  // Handle project modal
  const handleProjectClick = (project: ProjectUtilisation) => {
    setSelectedProjectModal(project);
  };

  // Handle filter to employee from modal
  const handleFilterToEmployee = (employeeId: string) => {
    setSelectedEmployees([employeeId]);
    setActivePresetId(null);
    toast.success('Filtered to selected employee');
  };

  // Handle filter to project from modal
  const handleFilterToProject = (projectId: string) => {
    // Note: Current filters don't support project filtering
    // This would require updating the filter structure
    toast.info('Project filtering coming soon');
    setActivePresetId(null);
  };

  // Get current filter state for preset manager
  const currentFilterState: FilterPreset['filters'] = {
    dateMode,
    selectedRange: dateMode === 'preset' ? selectedRange : undefined,
    customDateRange: dateMode === 'custom' ? customDateRange : undefined,
    selectedDepartment,
    selectedEmployees,
    selectedGroupBy,
  };

  // Employee options for multi-select
  const employeeOptions = useMemo(() => {
    return employees.map((emp) => ({
      value: emp.id,
      label: emp.name,
    }));
  }, [employees]);

  // Calculate advanced filter count (for badge)
  const advancedFilterCount = useMemo(() => {
    let count = 0;
    // Only count advanced filters (not quick filters)
    if (selectedDepartments.length > 0) count++;
    if (selectedProjects.length > 0) count++;
    if (billableOnly) count++;
    if (dateMode === 'custom') count++;
    if (selectedEmployees.length > 1) count++; // Multiple employees
    return count;
  }, [selectedDepartments, selectedProjects, billableOnly, dateMode, selectedEmployees]);

  // Handle remove individual filter
  const handleRemoveFilter = (filterType: string, value?: string) => {
    switch (filterType) {
      case 'dateRange':
        setDateMode('preset');
        setSelectedRange('last30days');
        setCustomDateRange({ start: undefined, end: undefined });
        break;
      case 'employee':
        if (value) {
          setSelectedEmployees(prev => prev.filter(id => id !== value));
          if (quickFilterEmployee === value) setQuickFilterEmployee('all');
        }
        break;
      case 'department':
        if (value) {
          setSelectedDepartments(prev => prev.filter(d => d !== value));
          if (selectedDepartment === value) setSelectedDepartment('all');
        }
        break;
      case 'project':
        if (value) {
          setSelectedProjects(prev => prev.filter(id => id !== value));
        }
        break;
      case 'billableOnly':
        setBillableOnly(false);
        break;
      case 'groupBy':
        setSelectedGroupBy('employee');
        break;
    }
    setActivePresetId(null);
  };

  // Handle clear all filters
  const handleClearAllFilters = () => {
    setDateMode('preset');
    setSelectedRange('last30days');
    setCustomDateRange({ start: undefined, end: undefined });
    setSelectedEmployees([]);
    setSelectedDepartments([]);
    setSelectedProjects([]);
    setSelectedDepartment('all');
    setQuickFilterEmployee('all');
    setBillableOnly(false);
    setSelectedGroupBy('employee');
    setActivePresetId(null);
    toast.success('All filters cleared');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <TrendingUp className="h-8 w-8" />
            Utilisation Report
          </h1>
          <p className="text-muted-foreground mt-2">
            Resource utilisation metrics and trends
          </p>
        </div>
        <Button onClick={handleExport} className="gap-2">
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
      </div>

      {/* Filters - Collapsible (Space Optimized) */}
      <div className="space-y-0 border rounded-lg overflow-hidden">
        {/* Header (Always Visible) */}
        <div className="flex items-center justify-between px-4 py-2 bg-muted/30">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              aria-label={filtersExpanded ? 'Collapse filters' : 'Expand filters'}
            >
              {filtersExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
            <Filter className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-sm font-medium">Filters</span>
            {advancedFilterCount > 0 && (
              <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                {advancedFilterCount}
              </Badge>
            )}
          </div>
          
          {advancedFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={handleClearAllFilters}
            >
              Clear All
            </Button>
          )}
        </div>
        
        {/* Filter Controls (Collapsible) */}
        {filtersExpanded && (
          <div className="px-4 py-3 border-t bg-background">
            <div className="space-y-4">
              {/* Filter Preset Manager */}
              <div className="pb-4 border-b">
                <FilterPresetManager
                  currentFilters={currentFilterState}
                  activePresetId={activePresetId}
                  onLoadPreset={handleLoadPreset}
                  onClearPreset={handleClearPreset}
                />
              </div>

              {/* Main Filters */}
              <div className="grid gap-4 md:grid-cols-4">
                {/* Date Range with Mode Toggle */}
                <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Date Range</label>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={() => {
                      setDateMode(dateMode === 'preset' ? 'custom' : 'preset');
                      setActivePresetId(null);
                    }}
                  >
                    {dateMode === 'preset' ? 'Custom' : 'Presets'}
                  </Button>
                </div>
                
                {dateMode === 'preset' ? (
                  <>
                    <Select
                      value={selectedRange}
                      onValueChange={(value) => {
                        setSelectedRange(value as keyof typeof DATE_RANGES);
                        setActivePresetId(null);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(DATE_RANGES).map(([key, { label }]) => (
                          <SelectItem key={key} value={key}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {format(dateRange.start, 'MMM d, yyyy')} - {format(dateRange.end, 'MMM d, yyyy')}
                    </p>
                  </>
                ) : (
                  <DateRangePicker
                    value={customDateRange}
                    onChange={(range) => {
                      setCustomDateRange(range);
                      setActivePresetId(null);
                    }}
                    maxDate={new Date()}
                  />
                )}
              </div>

                {/* Employee Multi-Select */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Employees</label>
                <MultiSelect
                  options={employeeOptions}
                  value={selectedEmployees}
                  onValueChange={(value) => {
                    setSelectedEmployees(value);
                    setActivePresetId(null);
                  }}
                  placeholder="All employees"
                  emptyMessage="No employees found"
                  maxDisplay={2}
                />
              </div>

                {/* Department Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Department</label>
                <Select
                  value={selectedDepartment}
                  onValueChange={(value) => {
                    setSelectedDepartment(value);
                    setActivePresetId(null);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem key={dept} value={dept}>
                        {dept}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

                {/* Group By */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Group By</label>
                <Select
                  value={selectedGroupBy}
                  onValueChange={(value) => {
                    setSelectedGroupBy(value as 'employee' | 'department' | 'project');
                    setActivePresetId(null);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="department">Department</SelectItem>
                    <SelectItem value="project">Project</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          </div>
        )}
      </div>

      {/* Summary Cards - Interactive */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card 
          className={cn(
            "cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105",
            "border-l-4",
            getUtilizationAccent(summary.averageUtilisation),
            "border-l-[var(--accent-border)]"
          )}
          onClick={() => handleSummaryCardClick('underutilized')}
        >
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <div className={cn(
                "p-1.5 rounded-md",
                getUtilizationAccent(summary.averageUtilisation),
                "bg-[var(--accent-bg-outline)]"
              )}>
                <Target className={cn(
                  "h-4 w-4",
                  getUtilizationAccent(summary.averageUtilisation),
                  "text-[var(--accent-text-on-outline)]"
                )} />
              </div>
              Average Utilisation
              <MousePointerClick className="h-3 w-3 ml-auto opacity-50" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <div className={cn(
                "text-2xl font-bold",
                getUtilizationAccent(summary.averageUtilisation),
                "text-[var(--accent-text-on-outline)]"
              )}>
                {(summary.averageUtilisation * 100).toFixed(1)}%
              </div>
              <Badge
                variant={summary.varianceFromTarget >= 0 ? 'default' : 'destructive'}
                className="text-xs"
              >
                {summary.varianceFromTarget >= 0 ? (
                  <TrendingUp className="h-3 w-3 mr-1" />
                ) : (
                  <TrendingDown className="h-3 w-3 mr-1" />
                )}
                {Math.abs(summary.varianceFromTarget * 100).toFixed(1)}%
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Target: {(summary.targetUtilisation * 100).toFixed(0)}%
            </p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105 border-l-4 accent-info border-l-[var(--accent-border)]"
          onClick={() => handleSummaryCardClick('employees')}
        >
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <div className="p-1.5 rounded-md accent-info bg-[var(--accent-bg-outline)]">
                <Clock className="h-4 w-4 accent-info text-[var(--accent-text-on-outline)]" />
              </div>
              Total Hours
              <MousePointerClick className="h-3 w-3 ml-auto opacity-50" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold accent-info text-[var(--accent-text-on-outline)]">
              {summary.totalHours.toFixed(1)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {summary.totalEmployees} {summary.totalEmployees === 1 ? 'employee' : 'employees'}
            </p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105 border-l-4 accent-success border-l-[var(--accent-border)]"
          onClick={() => handleSummaryCardClick('billable')}
        >
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <div className="p-1.5 rounded-md accent-success bg-[var(--accent-bg-outline)]">
                <DollarSign className="h-4 w-4 accent-success text-[var(--accent-text-on-outline)]" />
              </div>
              Billable Hours
              <MousePointerClick className="h-3 w-3 ml-auto opacity-50" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <div className="text-2xl font-bold accent-success text-[var(--accent-text-on-outline)]">
                {summary.totalBillableHours.toFixed(1)}
              </div>
              <Badge variant="secondary" className="text-xs">
                {summary.billablePercentage.toFixed(0)}%
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {summary.totalNonBillableHours.toFixed(1)} non-billable
            </p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105 border-l-4 accent-purple border-l-[var(--accent-border)]"
          onClick={() => handleSummaryCardClick('peak')}
        >
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <div className="p-1.5 rounded-md accent-purple bg-[var(--accent-bg-outline)]">
                <Activity className="h-4 w-4 accent-purple text-[var(--accent-text-on-outline)]" />
              </div>
              Peak Activity
              <MousePointerClick className="h-3 w-3 ml-auto opacity-50" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold accent-purple text-[var(--accent-text-on-outline)] capitalize">
              {summary.peakUtilisationDay}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Lowest: {summary.lowestUtilisationDay}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="employees">Employees</TabsTrigger>
          <TabsTrigger value="projects">Projects</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {/* Daily Hours Bar Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Daily Hours Breakdown</CardTitle>
              <CardDescription>Billable vs Non-Billable hours by day of week</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dailyChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="day" 
                    className="text-xs"
                    tick={{ fill: 'currentColor' }}
                  />
                  <YAxis 
                    className="text-xs"
                    tick={{ fill: 'currentColor' }}
                    label={{ value: 'Hours', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="Billable" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Non-Billable" fill="#6b7280" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-2">
            {/* Daily Utilisation Progress Bars */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Daily Utilisation Rate</CardTitle>
                <CardDescription>Utilisation percentage by day</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {reportData.dailyUtilisation.map((day) => (
                    <div key={day.day} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium capitalize">{day.day}</span>
                        <span className="text-muted-foreground">
                          {day.totalHours.toFixed(1)}h ({(day.utilisationRate * 100).toFixed(0)}%)
                        </span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className={cn(
                            'h-full transition-all',
                            day.utilisationRate >= 0.75
                              ? 'bg-green-500'
                              : day.utilisationRate >= 0.5
                                ? 'bg-yellow-500'
                                : 'bg-red-500'
                          )}
                          style={{ width: `${day.utilisationRate * 100}%` }}
                        />
                      </div>
                      <div className="flex gap-2 text-xs text-muted-foreground">
                        <span>Billable: {day.billableHours.toFixed(1)}h</span>
                        <span>•</span>
                        <span>Non-billable: {day.nonBillableHours.toFixed(1)}h</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Department Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Department Summary</CardTitle>
                <CardDescription>Utilisation by department</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <ScrollAreaViewport className="h-full pr-4">
                    <div className="space-y-4">
                      {Object.entries(departmentSummaries).map(([dept, summary]) => (
                        <div key={dept} className="space-y-2 pb-4 border-b last:border-0">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{dept}</span>
                            <Badge variant="secondary">
                              {summary.employeeCount}{' '}
                              {summary.employeeCount === 1 ? 'employee' : 'employees'}
                            </Badge>
                          </div>
                          <div className="space-y-1">
                            <div className="flex justify-between text-sm text-muted-foreground">
                              <span>Utilisation</span>
                              <span className="font-medium">
                                {(summary.avgUtilisation * 100).toFixed(1)}%
                              </span>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div
                                className={cn(
                                  'h-full transition-all',
                                  summary.avgUtilisation >= 0.75
                                    ? 'bg-green-500'
                                    : summary.avgUtilisation >= 0.5
                                      ? 'bg-yellow-500'
                                      : 'bg-red-500'
                                )}
                                style={{ width: `${summary.avgUtilisation * 100}%` }}
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-muted-foreground">Total: </span>
                              <span className="font-medium">{summary.totalHours.toFixed(1)}h</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Billable: </span>
                              <span className="font-medium text-green-600 dark:text-green-400">
                                {summary.billableHours.toFixed(1)}h
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollAreaViewport>
                  <ScrollAreaScrollbar orientation="vertical" />
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Employees Tab */}
        <TabsContent value="employees">
          <Card>
            <CardHeader>
              <CardTitle>Employee Utilisation</CardTitle>
              <CardDescription>
                Detailed utilisation metrics for each employee
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[600px]">
                <ScrollAreaViewport className="h-full pr-4">
                  <div className="space-y-4">
                    {reportData.employeeMetrics
                      .sort((a, b) => b.utilisationRate - a.utilisationRate)
                      .map((metric) => (
                        <Card 
                          key={metric.employeeId} 
                          className="border-2 cursor-pointer hover:shadow-lg transition-all duration-200"
                          onClick={() => handleEmployeeClick(metric)}
                        >
                          <CardContent className="pt-6">
                            <div className="space-y-4">
                              {/* Header */}
                              <div className="flex items-start justify-between">
                                <div>
                                  <h3 className="font-semibold text-lg">{metric.employeeName}</h3>
                                  <p className="text-sm text-muted-foreground">
                                    {metric.department}
                                  </p>
                                </div>
                                <Badge
                                  variant={
                                    metric.utilisationRate >= 0.75
                                      ? 'default'
                                      : metric.utilisationRate >= 0.5
                                        ? 'secondary'
                                        : 'destructive'
                                  }
                                  className="text-base px-3 py-1"
                                >
                                  {(metric.utilisationRate * 100).toFixed(1)}%
                                </Badge>
                              </div>

                              {/* Progress Bar */}
                              <div className="space-y-1">
                                <div className="h-3 bg-muted rounded-full overflow-hidden">
                                  <div
                                    className={cn(
                                      'h-full transition-all',
                                      metric.utilisationRate >= 0.75
                                        ? 'bg-green-500'
                                        : metric.utilisationRate >= 0.5
                                          ? 'bg-yellow-500'
                                          : 'bg-red-500'
                                    )}
                                    style={{ width: `${metric.utilisationRate * 100}%` }}
                                  />
                                </div>
                              </div>

                              {/* Metrics Grid */}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2">
                                <div>
                                  <p className="text-xs text-muted-foreground">Total Hours</p>
                                  <p className="text-lg font-semibold">
                                    {metric.totalHours.toFixed(1)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Billable</p>
                                  <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                                    {metric.billableHours.toFixed(1)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Non-Billable</p>
                                  <p className="text-lg font-semibold">
                                    {metric.nonBillableHours.toFixed(1)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Projects</p>
                                  <p className="text-lg font-semibold">{metric.projectCount}</p>
                                </div>
                              </div>

                              {/* Additional Info */}
                              <div className="flex flex-wrap gap-4 pt-2 border-t text-sm">
                                <div>
                                  <span className="text-muted-foreground">Peak Day: </span>
                                  <span className="font-medium capitalize">{metric.peakDay}</span>
                                  <span className="text-muted-foreground">
                                    {' '}
                                    ({metric.peakHours.toFixed(1)}h)
                                  </span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Target: </span>
                                  <span className="font-medium">
                                    {metric.targetHours.toFixed(1)}h
                                  </span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Variance: </span>
                                  <span
                                    className={cn(
                                      'font-medium',
                                      metric.variance >= 0
                                        ? 'text-green-600 dark:text-green-400'
                                        : 'text-red-600 dark:text-red-400'
                                    )}
                                  >
                                    {metric.variance >= 0 ? '+' : ''}
                                    {metric.variance.toFixed(1)}h
                                  </span>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                </ScrollAreaViewport>
                <ScrollAreaScrollbar orientation="vertical" />
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Projects Tab */}
        <TabsContent value="projects">
          <Card>
            <CardHeader>
              <CardTitle>Project Utilisation</CardTitle>
              <CardDescription>Time allocation across projects</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[600px]">
                <ScrollAreaViewport className="h-full pr-4">
                  <div className="space-y-3">
                    {reportData.projectUtilisation
                      .sort((a, b) => b.totalHours - a.totalHours)
                      .map((project) => (
                        <Card 
                          key={project.projectId} 
                          className="border cursor-pointer hover:shadow-lg transition-all duration-200"
                          onClick={() => handleProjectClick(project)}
                        >
                          <CardContent className="pt-6">
                            <div className="space-y-3">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <h3 className="font-semibold">{project.projectName}</h3>
                                    {project.isBillable && (
                                      <Badge variant="default" className="text-xs">
                                        Billable
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground">
                                    {project.clientName}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <div className="text-lg font-bold">
                                    {project.totalHours.toFixed(1)}h
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {project.employeeCount}{' '}
                                    {project.employeeCount === 1 ? 'employee' : 'employees'}
                                  </div>
                                </div>
                              </div>

                              <div className="grid grid-cols-3 gap-4 text-sm">
                                <div>
                                  <p className="text-muted-foreground">Billable</p>
                                  <p className="font-semibold text-green-600 dark:text-green-400">
                                    {project.billableHours.toFixed(1)}h
                                  </p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Non-Billable</p>
                                  <p className="font-semibold">
                                    {(project.totalHours - project.billableHours).toFixed(1)}h
                                  </p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Utilisation</p>
                                  <p className="font-semibold">
                                    {(project.utilisationRate * 100).toFixed(1)}%
                                  </p>
                                </div>
                              </div>

                              <div className="h-2 bg-muted rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-primary transition-all"
                                  style={{ width: `${project.utilisationRate * 100}%` }}
                                />
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                </ScrollAreaViewport>
                <ScrollAreaScrollbar orientation="vertical" />
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="space-y-4">
          {reportData.trends.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-muted-foreground">
                  <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No trend data available for the selected period</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Utilisation Trend Line Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Utilisation Trend</CardTitle>
                  <CardDescription>Weekly utilisation percentage and billable hours over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={trendChartData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="week" 
                        className="text-xs"
                        tick={{ fill: 'currentColor' }}
                      />
                      <YAxis 
                        className="text-xs"
                        tick={{ fill: 'currentColor' }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--background))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '6px'
                        }}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="Utilisation %" 
                        stroke="#3b82f6" 
                        strokeWidth={2}
                        dot={{ r: 4, fill: '#3b82f6' }}
                        activeDot={{ r: 6 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="Billable Hours" 
                        stroke="#10b981" 
                        strokeWidth={2}
                        dot={{ r: 4, fill: '#10b981' }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Weekly Trend Cards */}
              <Card>
                <CardHeader>
                  <CardTitle>Weekly Breakdown</CardTitle>
                  <CardDescription>Detailed metrics for each week</CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px]">
                    <ScrollAreaViewport className="h-full pr-4">
                      <div className="space-y-4">
                        {reportData.trends.map((trend, index) => (
                        <Card key={index} className="border">
                          <CardContent className="pt-6">
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h3 className="font-semibold">
                                    Week ending {format(trend.weekEnding, 'MMM d, yyyy')}
                                  </h3>
                                  <p className="text-sm text-muted-foreground">
                                    {trend.employeeCount}{' '}
                                    {trend.employeeCount === 1 ? 'employee' : 'employees'}
                                  </p>
                                </div>
                                <Badge
                                  variant={
                                    trend.utilisationRate >= 0.75
                                      ? 'default'
                                      : trend.utilisationRate >= 0.5
                                        ? 'secondary'
                                        : 'destructive'
                                  }
                                  className="text-base px-3 py-1"
                                >
                                  {(trend.utilisationRate * 100).toFixed(1)}%
                                </Badge>
                              </div>

                              <div className="grid grid-cols-3 gap-4 text-sm">
                                <div>
                                  <p className="text-muted-foreground">Total Hours</p>
                                  <p className="text-lg font-semibold">
                                    {trend.totalHours.toFixed(1)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Billable</p>
                                  <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                                    {trend.billableHours.toFixed(1)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Non-Billable</p>
                                  <p className="text-lg font-semibold">
                                    {(trend.totalHours - trend.billableHours).toFixed(1)}
                                  </p>
                                </div>
                              </div>

                              <div className="h-3 bg-muted rounded-full overflow-hidden">
                                <div
                                  className={cn(
                                    'h-full transition-all',
                                    trend.utilisationRate >= 0.75
                                      ? 'bg-green-500'
                                      : trend.utilisationRate >= 0.5
                                        ? 'bg-yellow-500'
                                        : 'bg-red-500'
                                  )}
                                  style={{ width: `${trend.utilisationRate * 100}%` }}
                                />
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </ScrollAreaViewport>
                  <ScrollAreaScrollbar orientation="vertical" />
                </ScrollArea>
              </CardContent>
            </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Phase 2 Modals */}
      {selectedEmployeeModal && (
        <EmployeeDetailModal
          isOpen={!!selectedEmployeeModal}
          onClose={() => setSelectedEmployeeModal(null)}
          employee={selectedEmployeeModal}
          timesheets={timesheets}
          employees={employees}
          projects={projects}
          dateRange={dateRange}
          onFilterToEmployee={handleFilterToEmployee}
        />
      )}

      {selectedProjectModal && (
        <ProjectDetailModal
          isOpen={!!selectedProjectModal}
          onClose={() => setSelectedProjectModal(null)}
          project={selectedProjectModal}
          timesheets={timesheets}
          employees={employees}
          projects={projects}
          dateRange={dateRange}
          onFilterToProject={handleFilterToProject}
        />
      )}
    </div>
  );
};
